import CustomerDialogRouter from 'components/CustomerDialog/CustomerDialog';
import { JydacomAssignmentsDialog } from 'components/JydacomAssignmentsDialog/JydacomAssignmentsDialog';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, withRouter } from 'react-router-dom';
import {
  selectAssignmentsByProjectId,
  selectIsJydacomAssignmentsDialogOpen,
  selectSelectedProjectsAssignmentsArrayWithoutDeleted,
} from 'store/assignments/selectors/assignment.selectors';
import { loadActiveAssignmentsForProject } from 'store/assignments/thunks/loadActiveAssignmentsForProject';
import { saveCreatedAndUpdatedProjectAssignments } from 'store/assignments/thunks/saveCreatedAndUpdatedProjectAssignments.thunk';
import {
  selectCustomBusinessCompetencyTypesArray,
  selectIsLoadingBusinessCompetencyTypes,
} from 'store/cards/selectors/cards.selectors';
import { patchDeleteCompetencyType } from 'store/cards/services/patchDeleteCompetencyType';
import { postNewCompetencyType } from 'store/cards/services/post-new-competency-type.service';
import { loadBusinessCompetencyTypes } from 'store/cards/thunks/loadBusinessCompetencyTypes.thunk';
import { selectCustomerArrayByBusinessId } from 'store/customer/selectors/customer.selectors';
import { loadCustomersByBusinessId } from 'store/customer/thunks/loadCustomersByBusinessId.thunk';
import { selectSelectedProject } from 'store/projects/selectors/projectsSelectors';
import { selectProjectCreationParentProjectOptions } from 'store/projects/selectors/userProjects.selectors';
import { createProject } from 'store/projects/thunks/createProject.thunk';
import { deleteProject } from 'store/projects/thunks/deleteProject.thunk';
import { loadBusinessIntegrationPartners } from 'store/projects/thunks/loadBusinessIntegrationPartners.thunk';
import { updateProject } from 'store/projects/thunks/updateProject.thunk';
import { useUploadFile } from 'utils/hooks/useUploadFile';
import uuidv4 from 'uuid/v4';
import ProjectDialogLayout from './ProjectDialogLayout';
import { selectIsEmployerAllowanceDrafts } from 'store/employerAllowances/selectors/employerAllowances.selectors';
import { createOrUpdateEmployerAllowancesFromDrafts } from 'store/employerAllowances/thunks/createOrUpdateEmployerAllowancesFromDrafts.thunk';

function ProjectDialog(props) {
  const dispatch = useDispatch();
  const { t } = useTranslation('common');

  const create = props.type === 'create';

  const projects = useSelector(selectProjectCreationParentProjectOptions);

  const projectFromSelector = useSelector(selectSelectedProject);

  const project = useMemo(() => {
    if (props.project) {
      return props.project;
    } else {
      return projectFromSelector;
    }
  }, [props.project, projectFromSelector]);

  const [parent, setParent] = useState(
    create ? project.id : project.parent || null
  );

  const [createCustomerDialogOpen, setCreateCustomerDialogOpen] =
    useState(false);

  const parentObject = useMemo(() => {
    return projects.find((p) => p.id === parent);
  }, [projects, parent]);

  const businessId = parentObject?.business_id || project.business_id;

  const { uploadFileAndFilePermission } = useUploadFile();

  const assignments = useSelector(
    selectSelectedProjectsAssignmentsArrayWithoutDeleted
  );

  const parentAssignments = useSelector(selectAssignmentsByProjectId(parent));
  const allBusinessCompetencyTypes = useSelector(
    selectCustomBusinessCompetencyTypesArray
  );

  const isJydacomAssignmentsDialogOpen = useSelector(
    selectIsJydacomAssignmentsDialogOpen
  );

  const employerAllowanceDraftsExist = useSelector(
    selectIsEmployerAllowanceDrafts
  );
  const isLoadingBusinessCompetencyTypes = useSelector(
    selectIsLoadingBusinessCompetencyTypes
  );
  const companyMode = project?.businessTypeEnum === 'EMPLOYER' && !create;
  const projectCompetencyTypes = useMemo(() => {
    if (!businessId) {
      return [];
    }

    return allBusinessCompetencyTypes.filter(
      (competencyType) => competencyType.businessId === businessId
    );
  }, [allBusinessCompetencyTypes, businessId]);

  useEffect(() => {
    dispatch(loadActiveAssignmentsForProject(project.id));
  }, []);

  useEffect(() => {
    dispatch(loadBusinessIntegrationPartners());
    dispatch(loadCustomersByBusinessId(businessId));
  }, [businessId]);

  useEffect(() => {
    // Load business competency types whenever a business is known so the
    // required-competency-cards picker has the employer's custom options
    // available on every project (not just employer/company-mode projects).
    if (businessId) {
      dispatch(loadBusinessCompetencyTypes());
    }
  }, [businessId, dispatch]);

  const customerOptions = useSelector(
    selectCustomerArrayByBusinessId(businessId)
  );

  useEffect(() => {
    if (parent) {
      dispatch(loadActiveAssignmentsForProject(parent));
    }
  }, [parent]);

  const [loading, setLoading] = useState(false);

  const history = useHistory();

  const { enqueueSnackbar } = useSnackbar();

  const uploadPicture = async (image, fileName) => {
    const filePermissionKey = `profile-images/${fileName}`;
    const filePermission = {
      id: uuidv4(),
      name: fileName,
      file: null,
      key: filePermissionKey,
      description: '',
      uploadSource: '',
      type: 'FILE',
    };

    return await uploadFileAndFilePermission({
      file: image,
      filePermission: filePermission,
      path: filePermissionKey,
      fileName: fileName,
      ignoreExtension: true,
    });
  };

  const saveCompanyCompetencyTypes = async (competencyTypes) => {
    if (!companyMode || !businessId) {
      return true;
    }

    const deletedCompetencyTypes = competencyTypes.filter(
      (competencyType) => competencyType.deletedAt && !competencyType.isNew
    );
    const createdCompetencyTypes = competencyTypes.filter(
      (competencyType) =>
        competencyType.isNew &&
        !competencyType.deletedAt &&
        competencyType.name?.toString().trim()
    );

    if (!deletedCompetencyTypes.length && !createdCompetencyTypes.length) {
      return true;
    }

    try {
      for (const competencyType of deletedCompetencyTypes) {
        await patchDeleteCompetencyType(competencyType.id);
      }

      for (const competencyType of createdCompetencyTypes) {
        await postNewCompetencyType({
          name: competencyType.name.trim(),
          businessIds: [businessId],
        });
      }

      await dispatch(loadBusinessCompetencyTypes(project.id)).unwrap();

      return true;
    } catch (error) {
      console.log('COMPETENCY TYPE SAVE FAILED:', error);
      enqueueSnackbar(
        t('project_dialog.snackbar_project_competency_types_update_failed'),
        {
          variant: 'error',
        }
      );
      return false;
    }
  };

  const onCreate = async (
    projectData,
    image,
    fileName,
    imageUpdated,
    assignments,
    createdAssignmentIds,
    competencyTypes
  ) => {
    try {
      console.log('Creating project', projectData);
      setLoading(true);

      const createdAssignments = assignments.filter((a) =>
        createdAssignmentIds.includes(a.id)
      );
      console.log('Created assignments:', createdAssignments);

      const projectResponse = await dispatch(
        createProject({ projectData, assignments: createdAssignments })
      ).then((response) => response);

      if (projectResponse.payload) {
        if (image && imageUpdated) {
          try {
            const resp = await uploadPicture(image, fileName);
            console.log('Image uploaded', resp);
          } catch (error) {
            enqueueSnackbar(
              t('project_dialog.snackbar_project_image_upload_failed'),
              {
                variant: 'error',
              }
            );
          }
        }
        const competencyTypesSaved = await saveCompanyCompetencyTypes(
          competencyTypes || []
        );

        if (competencyTypesSaved) {
          enqueueSnackbar(t('project_dialog.snackbar_project_created'), {
            variant: 'success',
          });
          props.onClose();
          if (props.redirect) {
            history.replace(
              `/project/${projectResponse.payload.newProject?.projectKey}`
            );
          }
        }
      }
    } catch (e) {
      console.log('CREATION FAILED:', e);
      enqueueSnackbar(t('project_dialog.snackbar_project_create_failed'), {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const onUpdate = async (
    projectData,
    image,
    fileName,
    imageUpdated,
    assignments,
    createdAssignmentIds,
    updatedAssignmentIds,
    competencyTypes
  ) => {
    try {
      console.log('Updating project', projectData);
      setLoading(true);

      // Update project
      const projectResponse = await dispatch(updateProject(projectData)).then(
        (response) => response
      );

      if (projectResponse.payload) {
        // Update assignments if they have changed
        if (
          createdAssignmentIds.length > 0 ||
          updatedAssignmentIds.length > 0
        ) {
          try {
            const createdAssignments = assignments.filter((a) =>
              createdAssignmentIds.includes(a.id)
            );
            const updatedAssignments = assignments.filter((a) =>
              updatedAssignmentIds.includes(a.id)
            );
            const assignmentResponse = await dispatch(
              saveCreatedAndUpdatedProjectAssignments({
                projectId: projectData.id,
                createdAssignments,
                updatedAssignments,
              })
            ).then((response) => response);
            console.log('ASSIGNMENT CREATION SUCCESS:', assignmentResponse);
          } catch (e) {
            console.log('ASSIGNMENT CREATION FAILED:', e);
            enqueueSnackbar(
              t('project_dialog.snackbar_project_assignments_update_failed'),
              {
                variant: 'error',
              }
            );
          }
        }

        // Upload image if it has been updated
        if (image && imageUpdated) {
          try {
            const resp = await uploadPicture(image, fileName);
            console.log('Image uploaded', resp);
          } catch (error) {
            enqueueSnackbar(
              t('project_dialog.snackbar_project_image_upload_failed'),
              {
                variant: 'error',
              }
            );
          }
        }

        if (employerAllowanceDraftsExist) {
          try {
            await dispatch(createOrUpdateEmployerAllowancesFromDrafts()).then(
              (response) => response
            );
          } catch (error) {
            enqueueSnackbar(t('project_dialog.snackbar_salary_update_failed'), {
              variant: 'error',
            });
          }
        }
        const competencyTypesSaved = await saveCompanyCompetencyTypes(
          competencyTypes || []
        );

        if (competencyTypesSaved) {
          // Show success message and close dialog
          enqueueSnackbar(t('project_dialog.snackbar_project_updated'), {
            variant: 'success',
          });
          props.onClose();
          if (image && imageUpdated && props.redirect) {
            window.location.reload();
          }
        }
      }
    } catch (e) {
      console.log('CREATION FAILED:', e);
      enqueueSnackbar(t('project_dialog.snackbar_project_update_failed'), {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const onDelete = async (project) => {
    try {
      console.log('Deleting project', project);
      setLoading(true);

      const projectId = project.id;

      // Update project
      const projectResponse = await dispatch(deleteProject(projectId)).then(
        (response) => response
      );
      console.log('DELETION RESPONSE:', projectResponse);
      console.log('DELETION PAYLOAD:', projectResponse.payload);

      if (projectResponse.payload) {
        // Show success message and close dialog
        enqueueSnackbar(t('project_dialog.snackbar_project_deleted'), {
          variant: 'success',
        });
        props.onClose();
        if (projectResponse.payload.parentProject?.projectKey) {
          history.replace(
            `/project/${projectResponse.payload.parentProject.projectKey}`
          );
        } else {
          history.replace('');
        }
      }
    } catch (e) {
      console.log('DELETION FAILED:', e);
      enqueueSnackbar(t('project_dialog.snackbar_project_delete_failed'), {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <ProjectDialogLayout
        onClose={props.onClose}
        loading={loading}
        open={props.open}
        project={create ? {} : project}
        businessId={businessId}
        projectCompetencyTypes={projectCompetencyTypes}
        isLoadingBusinessCompetencyTypes={isLoadingBusinessCompetencyTypes}
        projects={projects}
        parentProject={parent}
        parentObject={parentObject || {}}
        setParentProject={setParent}
        assignments={create ? parentAssignments : assignments}
        type={props.type}
        onCreate={onCreate}
        onUpdate={onUpdate}
        onDelete={onDelete}
        companyMode={companyMode}
        customerOptions={customerOptions}
        openCustomerDialog={() => setCreateCustomerDialogOpen(true)}
      />
      {createCustomerDialogOpen && (
        <CustomerDialogRouter
          open={createCustomerDialogOpen}
          onClose={() => setCreateCustomerDialogOpen(false)}
          type={'create'}
        />
      )}
      {isJydacomAssignmentsDialogOpen ? (
        <JydacomAssignmentsDialog
          projectId={project.id}
        ></JydacomAssignmentsDialog>
      ) : null}
    </>
  );
}

ProjectDialog.defaultProps = {
  type: 'create',
  redirect: false,
  project: undefined,
};

ProjectDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  type: PropTypes.oneOf(['create', 'edit']).isRequired,
  redirect: PropTypes.bool,
  project: PropTypes.object,
};

const ProjectDialogRouter = withRouter(ProjectDialog);

export default ProjectDialogRouter;
